import * as React from "react";
import { Menu } from "@base-ui/react/menu";
import { cn, type WithStringClassName } from "./Common/cn";

/**
 * v3 Action Menu surface, rendered with CSS classes from menu.css (imported via
 * @sproutsocial/seeds-react-menu/base/css) instead of styled-components. Each
 * export keeps its previous name and forwards props/refs to the underlying
 * Base UI Menu component.
 */

// Trigger had no styles of its own; keep it as a thin pass-through so the
// existing render/disabled props still reach Menu.Trigger.
export const ActionMenuTrigger = Menu.Trigger;

export const ActionMenuPositioner = React.forwardRef<
  React.ElementRef<typeof Menu.Positioner>,
  WithStringClassName<React.ComponentProps<typeof Menu.Positioner>>
>(({ className, ...rest }, ref) => (
  <Menu.Positioner
    ref={ref}
    className={cn("seeds-action-menu-positioner", className)}
    {...rest}
  />
));
ActionMenuPositioner.displayName = "ActionMenuPositioner";

// Opt out of Base UI Drawer's swipe-to-dismiss pointer capture when the menu
// is rendered inside a Drawer. Without this, the Drawer's viewport captures
// pointermove/up on portaled popups and MenuItem clicks never fire.
export const ActionMenuPopup = React.forwardRef<
  React.ElementRef<typeof Menu.Popup>,
  WithStringClassName<React.ComponentProps<typeof Menu.Popup>>
>(({ className, ...rest }, ref) => (
  <Menu.Popup
    ref={ref}
    data-base-ui-swipe-ignore=""
    className={cn("seeds-action-menu-popup", className)}
    {...rest}
  />
));
ActionMenuPopup.displayName = "ActionMenuPopup";

export const ActionMenuGroup = React.forwardRef<
  React.ElementRef<typeof Menu.Group>,
  WithStringClassName<React.ComponentProps<typeof Menu.Group>>
>(({ className, ...rest }, ref) => (
  <Menu.Group
    ref={ref}
    className={cn("seeds-action-menu-group", className)}
    {...rest}
  />
));
ActionMenuGroup.displayName = "ActionMenuGroup";

export const ActionMenuGroupLabel = React.forwardRef<
  React.ElementRef<typeof Menu.GroupLabel>,
  WithStringClassName<React.ComponentProps<typeof Menu.GroupLabel>>
>(({ className, ...rest }, ref) => (
  <Menu.GroupLabel
    ref={ref}
    className={cn("seeds-action-menu-group-label", className)}
    {...rest}
  />
));
ActionMenuGroupLabel.displayName = "ActionMenuGroupLabel";

export const ActionMenuSeparator = React.forwardRef<
  React.ElementRef<typeof Menu.Separator>,
  WithStringClassName<React.ComponentProps<typeof Menu.Separator>>
>(({ className, ...rest }, ref) => (
  <Menu.Separator
    ref={ref}
    className={cn("seeds-action-menu-separator", className)}
    {...rest}
  />
));
ActionMenuSeparator.displayName = "ActionMenuSeparator";

export const StyledMenuItem = React.forwardRef<
  React.ElementRef<typeof Menu.Item>,
  WithStringClassName<React.ComponentProps<typeof Menu.Item>>
>(({ className, ...rest }, ref) => (
  <Menu.Item
    ref={ref}
    className={cn("seeds-action-menu-item", className)}
    {...rest}
  />
));
StyledMenuItem.displayName = "StyledMenuItem";

export const StyledCheckboxItem = React.forwardRef<
  React.ElementRef<typeof Menu.CheckboxItem>,
  WithStringClassName<React.ComponentProps<typeof Menu.CheckboxItem>>
>(({ className, ...rest }, ref) => (
  <Menu.CheckboxItem
    ref={ref}
    className={cn(
      "seeds-action-menu-item",
      "seeds-action-menu-item--flex",
      className
    )}
    {...rest}
  />
));
StyledCheckboxItem.displayName = "StyledCheckboxItem";

export const StyledRadioItem = React.forwardRef<
  React.ElementRef<typeof Menu.RadioItem>,
  WithStringClassName<React.ComponentProps<typeof Menu.RadioItem>>
>(({ className, ...rest }, ref) => (
  <Menu.RadioItem
    ref={ref}
    className={cn(
      "seeds-action-menu-item",
      "seeds-action-menu-item--flex",
      className
    )}
    {...rest}
  />
));
StyledRadioItem.displayName = "StyledRadioItem";

export const StyledCheckboxItemIndicator = React.forwardRef<
  React.ElementRef<typeof Menu.CheckboxItemIndicator>,
  WithStringClassName<React.ComponentProps<typeof Menu.CheckboxItemIndicator>>
>(({ className, ...rest }, ref) => (
  <Menu.CheckboxItemIndicator
    ref={ref}
    className={cn("seeds-action-menu-item-indicator", className)}
    {...rest}
  />
));
StyledCheckboxItemIndicator.displayName = "StyledCheckboxItemIndicator";

export const StyledRadioItemIndicator = React.forwardRef<
  React.ElementRef<typeof Menu.RadioItemIndicator>,
  WithStringClassName<React.ComponentProps<typeof Menu.RadioItemIndicator>>
>(({ className, ...rest }, ref) => (
  <Menu.RadioItemIndicator
    ref={ref}
    className={cn("seeds-action-menu-item-indicator", className)}
    {...rest}
  />
));
StyledRadioItemIndicator.displayName = "StyledRadioItemIndicator";

export const StyledSubmenuTrigger = React.forwardRef<
  React.ElementRef<typeof Menu.SubmenuTrigger>,
  WithStringClassName<React.ComponentProps<typeof Menu.SubmenuTrigger>>
>(({ className, ...rest }, ref) => (
  <Menu.SubmenuTrigger
    ref={ref}
    className={cn(
      "seeds-action-menu-item",
      "seeds-action-menu-submenu-trigger",
      className
    )}
    {...rest}
  />
));
StyledSubmenuTrigger.displayName = "StyledSubmenuTrigger";

export const StyledLinkItem = React.forwardRef<
  React.ElementRef<typeof Menu.LinkItem>,
  WithStringClassName<React.ComponentProps<typeof Menu.LinkItem>>
>(({ className, ...rest }, ref) => (
  <Menu.LinkItem
    ref={ref}
    className={cn("seeds-action-menu-item", className)}
    {...rest}
  />
));
StyledLinkItem.displayName = "StyledLinkItem";
